import { EntityType, useAppSelector } from '@frontend/common';
import { Logger } from '@frontend/logger';
import { MqttBroker } from '@frontend/pub-sub';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useAccountRepository } from '../account/account-repository';
import { buildEntityMqttTopic, resolveEntityMqttAccountId } from '../mqtt/entity-mqtt-topic';
import { awaitResolve as asyncResolve } from './await-resolve';
import { ResolveOptions } from './resolve-function-registry';
import { getResolvedEntity, resolveStore, touch } from './resolve-slice';

interface UseReactiveObjectProps {
    id?: string;
    entity_type: EntityType;
    fetchOnload?: boolean;
    /** Skip resolve batch debounce + initial await delay (detail panes). */
    skipResolveTimeout?: boolean;
    resolveFunction: (id: string, options?: ResolveOptions) => string;
}
export interface UseReactiveObjectReturn<T> {
    object: T | null;
    initialising: boolean;
    isLoading: boolean;
}

/**
 * @description ReactiveObjectResolver must be present in the app for this to work.
 */
export function useReactiveObject<T extends { id: string }>({
    id,
    entity_type,
    fetchOnload = false,
    skipResolveTimeout = false,
    resolveFunction
}: UseReactiveObjectProps): UseReactiveObjectReturn<T> {
    const dispatch = useDispatch();
    const [ID] = useState<string>(() =>
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
                  const r = (Math.random() * 16) | 0;
                  return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
              })
    );
    const { currentAccount } = useAccountRepository();
    const resolveState = useAppSelector(useSelector, resolveStore);
    const resolveStateRef = useRef(resolveState);
    resolveStateRef.current = resolveState;
    const [initialising, setInitialising] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [object, setObject] = useState<T | null>(null);

    // Keep LRU timestamp fresh whenever this hook actively holds the object
    useEffect(() => {
        if (object && id) {
            dispatch(touch({ key: entity_type, id }));
        }
    }, [object]);

    const doFetch = async (): Promise<T | null> => {
        if (!id) return null;
        await asyncResolve(id, resolveFunction, skipResolveTimeout ? { skipTimeout: true } : undefined);
        return getResolvedEntity<T>(resolveStateRef.current, entity_type, id);
    };

    useEffect(() => {
        const init = async () => {
            if (!id) {
                setIsLoading(false);
                return;
            }
            let foundInState: T | null = null;
            if (!fetchOnload) {
                foundInState = getResolvedEntity<T>(resolveStateRef.current, entity_type, id);
                if (foundInState) {
                    setObject(foundInState);
                    return;
                }
            }

            //If not found in state, request it and wait for it to be resolved
            foundInState = await doFetch();
            if (foundInState) {
                setObject(foundInState);
            } else Logger.error('ReactiveObjectResolver: Object not found after request', { id, entity_type });
        };

        init().finally(() => {
            setInitialising(false);
            setIsLoading(false);
        });
    }, [id, entity_type]);

    const objectAccountId = (object as { account_id?: string } | null)?.account_id;
    const mqttAccountId = resolveEntityMqttAccountId(objectAccountId, currentAccount);

    useEffect(() => {
        if (!id || !mqttAccountId) return;
        const topic = buildEntityMqttTopic(mqttAccountId, entity_type, id);
        MqttBroker.getInstance().subscribe(ID, topic, (message: string, data: T | null) => {
            if (message.includes('updated')) {
                if (data && data.id === id) {
                    //just check to be sure
                    setObject(data);
                } else {
                    setIsLoading(true);
                    doFetch()
                        .then((u) => {
                            if (u) setObject(u);
                        })
                        .finally(() => {
                            setIsLoading(false);
                        });
                }
            } else if (message.includes('deleted')) {
                setObject(null);
            }
        });

        return () => {
            MqttBroker.getInstance().unSubscribe(ID, topic);
        };
    }, [id, mqttAccountId, entity_type, ID]);

    return { object, initialising, isLoading };
}

export interface UseReactiveObjectsProps {
    ids?: string[];
    entity_type: EntityType;
    fetchOnload?: boolean;
    resolveFunction: (id: string) => string;
}
export interface UseReactiveObjectsReturn<T> {
    objects: T[];
    initialising: boolean;
    isLoading: boolean;
}

/**
 * Multi-ID variant of useReactiveObject.
 * Fetches and keeps all listed entities up to date via the same event/cache/MQTT system.
 * @description The corresponding Resolver component must be present in the app for this to work.
 */
export function useReactiveObjects<T extends { id: string }>({
    ids,
    entity_type,
    fetchOnload = false,
    resolveFunction
}: UseReactiveObjectsProps): UseReactiveObjectsReturn<T> {
    const dispatch = useDispatch();
    const [ID] = useState<string>(() =>
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
                  const r = (Math.random() * 16) | 0;
                  return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
              })
    );
    const { currentAccount } = useAccountRepository();
    const resolveState = useAppSelector(useSelector, resolveStore);
    const resolveStateRef = useRef(resolveState);
    resolveStateRef.current = resolveState;
    const [initialising, setInitialising] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [objects, setObjects] = useState<T[]>([]);

    // Keep LRU timestamps fresh for all actively held objects
    useEffect(() => {
        objects.forEach((o) => dispatch(touch({ key: entity_type, id: o.id })));
    }, [objects]);

    // Stable string key so effects only re-run when the id list actually changes
    const idsKey = ids?.join(',') ?? '';

    useEffect(() => {
        const init = async () => {
            if (!ids || ids.length === 0) {
                setObjects([]);
                setIsLoading(false);
                return;
            }

            const found: T[] = [];
            const missing: string[] = [];

            for (const id of ids) {
                if (!fetchOnload) {
                    const cached = getResolvedEntity<T>(resolveStateRef.current, entity_type, id);
                    if (cached) {
                        found.push(cached);
                        continue;
                    }
                }
                missing.push(id);
            }

            if (missing.length > 0) {
                // Fire resolve events for all missing IDs — the resolver debounces and
                // batches them into a single API call within its 1-second window.
                await Promise.all(missing.map((id) => asyncResolve(id, resolveFunction)));

                for (const id of missing) {
                    const entity = getResolvedEntity<T>(resolveStateRef.current, entity_type, id);
                    if (entity) {
                        found.push(entity);
                    } else {
                        Logger.error('ReactiveObjectResolver: Object not found after request', { id, entity_type });
                    }
                }
            }

            setObjects(found);
        };

        init().finally(() => {
            setInitialising(false);
            setIsLoading(false);
        });
    }, [idsKey, entity_type]);

    // Prefer each entity's own account_id once resolved; fall back to currentAccount until then.
    const mqttAccountsKey = (ids ?? [])
        .map((entityId) => {
            const entity = objects.find((o) => o.id === entityId) as ({ account_id?: string } & T) | undefined;
            return `${entityId}:${entity?.account_id ?? currentAccount ?? ''}`;
        })
        .join(',');

    // Manage MQTT subscriptions — subscribe to all current IDs, clean up on change/unmount.
    useEffect(() => {
        if (!mqttAccountsKey) return;

        const topics = mqttAccountsKey
            .split(',')
            .map((entry) => {
                const [entityId, accountId] = entry.split(':');
                if (!entityId || !accountId) return null;
                return { entityId, topic: buildEntityMqttTopic(accountId, entity_type, entityId) };
            })
            .filter((entry): entry is { entityId: string; topic: string } => entry != null);

        for (const { entityId, topic } of topics) {
            MqttBroker.getInstance().subscribe(`${ID}:${entityId}`, topic, (message: string, data: T | null) => {
                if (message.includes('updated')) {
                    if (data && data.id === entityId) {
                        setObjects((prev) => prev.map((o) => (o.id === entityId ? data : o)));
                    } else {
                        asyncResolve(entityId, resolveFunction).then(() => {
                            const updated = getResolvedEntity<T>(resolveStateRef.current, entity_type, entityId);
                            if (updated) setObjects((prev) => prev.map((o) => (o.id === entityId ? updated : o)));
                        });
                    }
                } else if (message.includes('deleted')) {
                    setObjects((prev) => prev.filter((o) => o.id !== entityId));
                }
            });
        }

        return () => {
            for (const { entityId, topic } of topics) {
                MqttBroker.getInstance().unSubscribe(`${ID}:${entityId}`, topic);
            }
        };
    }, [mqttAccountsKey, entity_type, ID, resolveFunction]);

    return { objects, initialising, isLoading };
}
